import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { ArrowRight, Sparkles, Zap, Film, Users, Wand2, ChevronRight, Check, Lightbulb, Layers, Play, PenTool, Video, BookOpen, Globe } from "lucide-react";

// Astra comic panel images
const ASTRA_IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_a0bef7eb-8dbe-4ba3-9e4f-baa974afe302/artifacts/qzm9id5k_4a8c663cb80418dde0f8978f6a19560127cc19c2f9cbd6d2da4ef8d7c58c9b4b.jpeg",
  panel1: "https://customer-assets.emergentagent.com/job_a0bef7eb-8dbe-4ba3-9e4f-baa974afe302/artifacts/jdho3nx8_1b204de665d0e99704bd6227bb7dd5a30faeddb032bf920585301f5e0a563918.jpeg",
  panel2: "https://customer-assets.emergentagent.com/job_a0bef7eb-8dbe-4ba3-9e4f-baa974afe302/artifacts/k5cr5zbe_c56185df1d7efa347318b5b19614102099178112ceffdaadc852a7ecde6fdaf2.jpeg",
  panel3: "https://customer-assets.emergentagent.com/job_a0bef7eb-8dbe-4ba3-9e4f-baa974afe302/artifacts/uq20ydsf_8eb260eded8af19b0b85e37c9c01f5fc2cf419307e26af3482ee5432949bcbe0.jpeg",
  panel4: "https://customer-assets.emergentagent.com/job_a0bef7eb-8dbe-4ba3-9e4f-baa974afe302/artifacts/m8dn1173_065df06c201b6f19c2600b523e2d360b8258c797f6a1e49fa0801cc19e427b30.jpeg",
  // New panels
  panel5: "https://customer-assets.emergentagent.com/job_astra-visual-impact/artifacts/qzrr7gwh_panel_1%20%281%29.png",
  panel6: "https://customer-assets.emergentagent.com/job_astra-visual-impact/artifacts/e4l3j4ma_panel_1.png",
  panel7: "https://customer-assets.emergentagent.com/job_astra-visual-impact/artifacts/k6s7ufgq_panel_2.png",
  panel8: "https://customer-assets.emergentagent.com/job_astra-visual-impact/artifacts/ahzibjjb_panel_3.png",
};

// Floating Particles Component
const FloatingParticles = () => {
  return (
    <div className="particles-container">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="particle"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${3 + Math.random() * 4}s`,
          }}
        />
      ))}
      {[...Array(8)].map((_, i) => (
        <div
          key={`streak-${i}`}
          className="light-streak"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 8}s`,
            transform: `rotate(${Math.random() * 360}deg)`,
          }}
        />
      ))}
    </div>
  );
};

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_astra-visual-impact/artifacts/urdwxgtp_circle_cropped.png";

// Google Form URL
const GOOGLE_FORM_URL = "https://forms.gle/CGeMifsfXcRcpjxm7";

// Open Google Form in new tab
const openGoogleForm = () => {
  window.open(GOOGLE_FORM_URL, "_blank", "noopener,noreferrer");
};

// Navigation
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav className={`nav-container ${scrolled ? "nav-scrolled" : ""}`} data-testid="navigation">
      <div className="nav-content">
        <div className="nav-logo" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Astra Animation AI" className="logo-image" />
          <span className="logo-text">Astra Animation AI</span>
        </div>
        <div className="nav-links">
          <a href="#features" className="nav-link" data-testid="nav-features">Features</a>
          <a href="#showcase" className="nav-link" data-testid="nav-showcase">Showcase</a>
          <a href="#cta" className="nav-link" data-testid="nav-cta">Early Access</a>
        </div>
        <button className="nav-cta" data-testid="nav-cta-button" onClick={openGoogleForm}>
          Get Started <ArrowRight size={16} />
        </button>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePos({
          x: (e.clientX - rect.left - rect.width / 2) / 50,
          y: (e.clientY - rect.top - rect.height / 2) / 50,
        });
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section className="hero-section" ref={heroRef} data-testid="hero-section">
      {/* Background with zoom animation */}
      <div className="hero-bg">
        <img src={ASTRA_IMAGES.hero} alt="" className="hero-bg-image" />
        <div className="hero-overlay" />
      </div>

      {/* Floating Particles */}
      <FloatingParticles />

      {/* Hero Content */}
      <div className="hero-content">
        <div className="hero-left">
          {/* Badge */}
          <div className="hero-badge animate-fade-in" data-testid="hero-badge">
            <Sparkles size={14} />
            <span>India's First Comic & Narrative AI Platform</span>
          </div>

          {/* Main Headline */}
          <h1 className="hero-headline animate-fade-in-delay-1" data-testid="hero-headline">
            <span className="headline-line">Create Cinematic</span>
            <span className="headline-gradient">Comics with AI</span>
          </h1>

          {/* Subtext */}
          <p className="hero-subtext animate-fade-in-delay-2" data-testid="hero-subtext">
            Transform ideas into structured visual stories with AI.
            <br />
            Cinematic direction, character consistency, and complete storytelling.
          </p>

          {/* Enhanced Value Proposition */}
          <p className="hero-value-prop animate-fade-in-delay-2" data-testid="hero-value-prop">
            From idea to full cinematic comic — panels, characters, storytelling — all powered by AI.
          </p>

          {/* CTA Button */}
          <div className="hero-cta-container animate-fade-in-delay-3">
            <button className="hero-cta-button" data-testid="hero-cta-button" onClick={openGoogleForm}>
              <span>Get Early Access</span>
              <ArrowRight className="cta-arrow" />
            </button>
            
            {/* CTA Benefits */}
            <div className="cta-benefits" data-testid="cta-benefits">
              <div className="cta-benefit">
                <Check size={14} />
                <span>No design skills required</span>
              </div>
              <div className="cta-benefit">
                <Check size={14} />
                <span>Generate full comics in minutes</span>
              </div>
              <div className="cta-benefit">
                <Check size={14} />
                <span>Early users help shape the platform</span>
              </div>
            </div>

            {/* Urgency Text */}
            <p className="hero-urgency-text">Limited Early Access — Only first creators get priority features</p>
          </div>
        </div>

        {/* Floating Panels - Desktop Only */}
        <div className="hero-right">
          <div
            className="floating-panel panel-1"
            style={{
              transform: `translate(${mousePos.x * 2}px, ${mousePos.y * 2}px)`,
            }}
            data-testid="floating-panel-1"
          >
            <img src={ASTRA_IMAGES.panel2} alt="Astra Panel" />
          </div>
          <div
            className="floating-panel panel-2"
            style={{
              transform: `translate(${mousePos.x * -1.5}px, ${mousePos.y * -1.5}px)`,
            }}
            data-testid="floating-panel-2"
          >
            <img src={ASTRA_IMAGES.panel3} alt="Astra Panel" />
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="scroll-indicator" data-testid="scroll-indicator">
        <div className="scroll-mouse">
          <div className="scroll-wheel" />
        </div>
        <span>Scroll to explore</span>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  const features = [
    {
      icon: <Film />,
      title: "Cinematic Direction",
      description: "AI-powered camera angles, lighting, and composition for every panel.",
    },
    {
      icon: <Users />,
      title: "Character Consistency",
      description: "Maintain perfect character appearance across your entire story.",
    },
    {
      icon: <Wand2 />,
      title: "Smart Storytelling",
      description: "AI assists in plot development, pacing, and visual narrative flow.",
    },
    {
      icon: <Zap />,
      title: "Instant Generation",
      description: "Create complete comic pages in minutes, not hours.",
    },
  ];

  return (
    <section className="features-section" id="features" data-testid="features-section">
      <div className="features-container">
        <div className="section-header scroll-reveal">
          <span className="section-badge">
            <Sparkles size={14} />
            Features
          </span>
          <h2 className="section-title">
            Everything You Need to
            <span className="title-gradient"> Create Comics</span>
          </h2>
          <p className="section-subtitle">
            Powerful AI tools designed for storytellers and artists
          </p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div
              key={index}
              className="feature-card scroll-reveal"
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`feature-card-${index}`}
            >
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
              <div className="feature-glow" />
            </div>
          ))}
        </div>

        {/* Global Positioning */}
        <div className="global-positioning scroll-reveal">
          <Globe size={18} />
          <span>Competing with the world's best — built from India.</span>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      icon: <Lightbulb />,
      title: "Enter Your Idea",
      description: "Describe your story, characters, or scene in simple words.",
    },
    {
      number: "02",
      icon: <Layers />,
      title: "AI Generates Scenes",
      description: "Our AI structures your narrative into cinematic panels.",
    },
    {
      number: "03",
      icon: <Play />,
      title: "Get Comic Panels",
      description: "Receive polished, publication-ready comic artwork.",
    },
  ];

  return (
    <section className="how-it-works-section" id="how-it-works" data-testid="how-it-works-section">
      <div className="how-it-works-container">
        <div className="section-header scroll-reveal">
          <span className="section-badge">
            <Zap size={14} />
            Process
          </span>
          <h2 className="section-title">
            How It
            <span className="title-gradient"> Works</span>
          </h2>
          <p className="section-subtitle">
            Three simple steps to create your comic
          </p>
        </div>

        <div className="steps-container">
          {steps.map((step, index) => (
            <div
              key={index}
              className="step-card scroll-reveal"
              style={{ animationDelay: `${index * 0.15}s` }}
              data-testid={`step-card-${index}`}
            >
              <div className="step-number">{step.number}</div>
              <div className="step-icon">{step.icon}</div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-description">{step.description}</p>
              {index < steps.length - 1 && <div className="step-connector" />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Built For Section
const BuiltForSection = () => {
  const audiences = [
    { icon: <PenTool />, title: "Indie Creators", description: "Turn your stories into visual masterpieces" },
    { icon: <Wand2 />, title: "Comic Artists", description: "Accelerate your workflow with AI assistance" },
    { icon: <BookOpen />, title: "Writers", description: "See your narratives come to life visually" },
    { icon: <Video />, title: "Filmmakers", description: "Storyboard and visualize scenes instantly" },
  ];

  return (
    <section className="built-for-section" data-testid="built-for-section">
      <div className="built-for-container">
        <div className="section-header scroll-reveal">
          <span className="section-badge">
            <Users size={14} />
            Audience
          </span>
          <h2 className="section-title">
            Built for the Next Generation of
            <span className="title-gradient"> Storytellers</span>
          </h2>
        </div>

        <div className="audience-grid">
          {audiences.map((audience, index) => (
            <div
              key={index}
              className="audience-card scroll-reveal"
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`audience-card-${index}`}
            >
              <div className="audience-icon">{audience.icon}</div>
              <h3 className="audience-title">{audience.title}</h3>
              <p className="audience-description">{audience.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Showcase Section
const ShowcaseSection = () => {
  const panels = [
    { image: ASTRA_IMAGES.hero, title: "Origin Story", tag: "Scene Generation" },
    { image: ASTRA_IMAGES.panel1, title: "City Watch", tag: "Cinematic Lighting" },
    { image: ASTRA_IMAGES.panel2, title: "Power Unleashed", tag: "Action Composition" },
    { image: ASTRA_IMAGES.panel3, title: "The Guardian", tag: "Character Consistency" },
    { image: ASTRA_IMAGES.panel4, title: "Rising Force", tag: "Scene Generation" },
    { image: ASTRA_IMAGES.panel5, title: "Lab Discovery", tag: "Action Composition" },
    { image: ASTRA_IMAGES.panel6, title: "Blue Transformation", tag: "Character Consistency" },
    { image: ASTRA_IMAGES.panel7, title: "Action Landing", tag: "Cinematic Lighting" },
    { image: ASTRA_IMAGES.panel8, title: "Enemy Attack", tag: "Action Composition" },
  ];

  return (
    <section className="showcase-section" id="showcase" data-testid="showcase-section">
      <div className="showcase-container">
        <div className="section-header scroll-reveal">
          <span className="section-badge">
            <Film size={14} />
            Showcase
          </span>
          <h2 className="section-title">
            See What's
            <span className="title-gradient"> Possible</span>
          </h2>
          <p className="section-subtitle">
            AI-generated comic panels with cinematic quality
          </p>
        </div>

        <div className="showcase-scroll-container">
          <div className="showcase-track">
            {panels.map((panel, index) => (
              <div
                key={index}
                className="showcase-card scroll-reveal"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`showcase-card-${index}`}
              >
                <div className="showcase-image-wrapper">
                  <img src={panel.image} alt={panel.title} />
                  <div className="showcase-tag">{panel.tag}</div>
                  <div className="showcase-overlay">
                    <span className="showcase-title">{panel.title}</span>
                    <ChevronRight className="showcase-arrow" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  return (
    <section className="final-cta-section" id="cta" data-testid="final-cta-section">
      {/* Background */}
      <div className="cta-bg">
        <img src={ASTRA_IMAGES.panel2} alt="" className="cta-bg-image" />
        <div className="cta-overlay" />
      </div>

      {/* Animated Glow */}
      <div className="cta-glow-orb" />

      <div className="cta-content scroll-reveal">
        <h2 className="cta-headline" data-testid="cta-headline">
          The Future of Comics
          <span className="cta-gradient"> is Here</span>
        </h2>
        <p className="cta-subtext">
          Be among the first creators to shape the future of visual storytelling
        </p>
        <button className="cta-button" data-testid="final-cta-button" onClick={openGoogleForm}>
          <span>Get Early Access</span>
          <ArrowRight className="cta-arrow" />
        </button>
        <div className="cta-social-proof">
          <span>10+ creators joined in early access</span>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="footer" data-testid="footer">
      <div className="footer-content">
        <div className="footer-logo">
          <img src={LOGO_URL} alt="Astra Animation AI" className="footer-logo-image" />
          <span>Astra Animation AI</span>
        </div>
        <div className="footer-links">
          <a href="#features">Features</a>
          <a href="#showcase">Showcase</a>
          <a href="#cta">Early Access</a>
        </div>
        <p className="footer-copyright">
          © 2026 Astra Animation AI. All Rights Reserved.
        </p>
      </div>
    </footer>
  );
};

// Scroll Reveal Hook
const useScrollReveal = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    document.querySelectorAll(".scroll-reveal").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
};

// Main App
function App() {
  useScrollReveal();

  return (
    <div className="app" data-testid="app-container">
      <Navigation />
      <HeroSection />
      <FeaturesSection />
      <HowItWorksSection />
      <BuiltForSection />
      <ShowcaseSection />
      <FinalCTASection />
      <Footer />
    </div>
  );
}

export default App;
